/**
 * ThemesTab (Sprint 15) — dedicated CRUD surface for the project theme catalog.
 *
 * Layout:
 *   • Header: stats + [+ New theme] + [✨ Generate via AI] buttons
 *   • Section 1 — Canonical Theme Catalog (DynamicTable of applied themes)
 *                 row click → ThemeEditorDialog (edit)
 *                 actions menu → Delete with confirmation
 *   • Section 2 — Generation History (past AI runs + pending-review badges)
 *                 row click → GenerationReviewDialog (accept/reject → apply)
 *
 * Follows the audiences-style CRUD convention:
 *   DynamicTable → onRowClick → edit dialog → Save button → service.update
 *   (setDoc merge:true) → toast → real-time re-render via Firestore listener.
 */

import React, { useMemo, useState } from 'react';
import {
  Box, Stack, Typography, Button, Chip, Alert, Snackbar,
  IconButton, Menu, MenuItem, Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { DynamicTable, type TableColumn, BaseDialog, DialogHeader, DialogContent, DialogFooter } from '@modules/ui-components';
import { StrategyService } from '@modules/firebase-core/services/StrategyService';
import { useThemeCatalog } from '../hooks/useThemeCatalog';
import { useGenerationRuns, type GenerationRunSummary } from '../hooks/useGenerationRuns';
import { useGenerationDrafts, type GenerationDraft } from '../hooks/useGenerationDrafts';
import { GenerationHistoryList } from './generations/GenerationHistoryList';
import { GenerationReviewDialog } from './generations/GenerationReviewDialog';
import { ThemeGenerateDialog } from './ThemeGenerateDialog';
import { ThemeEditorDialog } from './ThemeEditorDialog';
import { ManualThemeDialog } from './theme/ManualThemeDialog';
import { DataUsageList } from './DataUsageList';
import { ContentPlanApiClient } from '../services/ContentPlanApiClient';
import type { ContentTheme } from '../types';

const TESTIDS = {
  root: 'themes-tab',
  newBtn: 'themes-new-btn',
  genAiBtn: 'themes-generate-ai-btn',
  catalogTable: 'themes-catalog-table',
  historySection: 'themes-history-section',
} as const;

export interface ThemesTabProps {
  projectId: string;
  userId: string;
  strategyService: StrategyService;
}

export const ThemesTab: React.FC<ThemesTabProps> = ({ projectId, userId, strategyService }) => {
  const { themes, loading, saveThemes } = useThemeCatalog(projectId);
  const { runs } = useGenerationRuns(projectId, 'theme');

  const [editorOpen, setEditorOpen] = useState(false);
  const [manualThemeOpen, setManualThemeOpen] = useState(false);
  const [editingTheme, setEditingTheme] = useState<ContentTheme | null>(null);
  const [generateOpen, setGenerateOpen] = useState(false);
  const [reviewRun, setReviewRun] = useState<GenerationRunSummary | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; theme: ContentTheme } | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<ContentTheme | null>(null);
  const [toast, setToast] = useState<{ msg: string; severity: 'success' | 'error' | 'info' } | null>(null);

  const existingIds = useMemo(() => themes.map((t) => t.id), [themes]);

  const handleCreate = () => { setManualThemeOpen(true); };
  const handleEdit = (theme: ContentTheme) => { setEditingTheme(theme); setEditorOpen(true); };

  const handleSaveTheme = async (next: ContentTheme) => {
    const existing = themes.find((t) => t.id === next.id);
    const merged = existing
      ? themes.map((t) => t.id === next.id ? next : t)
      : [...themes, next];
    await saveThemes(merged);
    setToast({ msg: existing ? `Theme "${next.name}" updated` : `Theme "${next.name}" created`, severity: 'success' });
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    const next = themes.filter((t) => t.id !== deleteConfirm.id);
    try {
      await saveThemes(next);
      setToast({ msg: `Theme "${deleteConfirm.name}" deleted`, severity: 'success' });
    } catch (e) {
      setToast({ msg: `Delete failed: ${(e as Error).message}`, severity: 'error' });
    } finally {
      setDeleteConfirm(null);
    }
  };

  const handleApplyReviewedThemes = async (acceptedItemIds: string[]) => {
    if (!reviewRun) return;
    const client = new ContentPlanApiClient();
    const res = await client.applyThemes({ projectId, generationId: reviewRun.id, acceptedItemIds });
    if (!res.success) throw new Error(res.error ?? 'apply failed');
    setToast({ msg: `Applied ${res.appliedCount} themes (${res.skippedCount} skipped)`, severity: 'success' });
  };

  const columns: TableColumn<ContentTheme>[] = [
    { key: 'name', label: 'Name', width: '22%' },
    { key: 'id', label: 'ID', width: '22%', format: (v: any) => <code style={{ fontSize: '0.85em' }}>{String(v)}</code> },
    {
      key: 'defaultMixPct', label: 'Mix %', align: 'center', width: '10%',
      format: (v: any) => typeof v === 'number' ? `${(v * 100).toFixed(0)}%` : '—',
    },
    {
      key: 'description', label: 'Description',
      format: (v: any) => v ? <Typography variant="caption">{String(v).slice(0, 120)}</Typography> : <Typography variant="caption" color="text.secondary">—</Typography>,
    },
    {
      key: '_actions' as any, label: '', align: 'center', width: '5%',
      format: (_: any, row: any) => (
        <IconButton
          size="small"
          onClick={(e) => { e.stopPropagation(); setMenuAnchor({ el: e.currentTarget, theme: row as ContentTheme }); }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  const mixTotal = themes.reduce((s, t) => s + (t.defaultMixPct ?? 0), 0);
  const pendingReviewCount = runs.filter((r) => r.status === 'completed' || r.status === 'reviewed').length;

  return (
    <Box data-testid={TESTIDS.root}>
      {/* Header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h6">Theme Catalog</Typography>
          <Typography variant="caption" color="text.secondary">
            {themes.length} themes · mix total: {mixTotal.toFixed(2)} (should be ~1.0)
            {pendingReviewCount > 0 && ` · ${pendingReviewCount} AI run(s) awaiting review`}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={handleCreate} data-testid={TESTIDS.newBtn}>
            New theme
          </Button>
          <Button variant="contained" startIcon={<AutoFixHighIcon />} onClick={() => setGenerateOpen(true)} data-testid={TESTIDS.genAiBtn}>
            Generate via AI
          </Button>
        </Stack>
      </Stack>

      {/* Section 1 — Canonical catalog */}
      <Box data-testid={TESTIDS.catalogTable} sx={{ mb: 4 }}>
        {loading ? (
          <Typography variant="body2">Loading…</Typography>
        ) : themes.length === 0 ? (
          <Alert severity="info">
            No themes in the catalog yet. Use <strong>Generate via AI</strong> to seed 5-10 themes,
            or <strong>New theme</strong> to add one manually.
          </Alert>
        ) : (
          <DynamicTable
            data={themes as any[]}
            columns={columns as any}
            keyField={'id' as any}
            onRowClick={(row) => handleEdit(row as ContentTheme)}
          />
        )}
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Section 2 — Generation history */}
      <Box data-testid={TESTIDS.historySection}>
        <Typography variant="h6" gutterBottom>Generation history</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
          Past AI runs. Click a run to review drafts and apply accepted ones to the catalog above.
        </Typography>
        <GenerationHistoryList
          runs={runs}
          onRowClick={(run) => setReviewRun(run)}
          emptyHint="No AI generation runs yet. Click 'Generate via AI' above to create one."
        />
      </Box>

      {/* Actions menu */}
      <Menu
        anchorEl={menuAnchor?.el}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
      >
        <MenuItem onClick={() => {
          if (menuAnchor) handleEdit(menuAnchor.theme);
          setMenuAnchor(null);
        }}>Edit</MenuItem>
        <MenuItem onClick={() => {
          if (menuAnchor) setDeleteConfirm(menuAnchor.theme);
          setMenuAnchor(null);
        }} sx={{ color: 'error.main' }}>Delete</MenuItem>
      </Menu>

      {/* Delete confirmation */}
      <BaseDialog open={Boolean(deleteConfirm)} onClose={() => setDeleteConfirm(null)} maxWidth="xs">
        <DialogHeader title="Delete theme?" onClose={() => setDeleteConfirm(null)} />
        <DialogContent>
          <Typography variant="body2">
            Delete theme <strong>{deleteConfirm?.name}</strong> from the catalog?
            Topics or sprints referencing it will keep the theme id as a string but lose metadata.
          </Typography>
        </DialogContent>
        <DialogFooter
          secondaryAction={{ label: 'Cancel', onClick: () => setDeleteConfirm(null) } as any}
          primaryAction={{ label: 'Delete', onClick: handleDelete, color: 'error' } as any}
        />
      </BaseDialog>

      {/* Editor (create + edit) */}
      <ThemeEditorDialog
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        theme={editingTheme}
        existingIds={existingIds}
        onSave={handleSaveTheme}
      />

      <ManualThemeDialog
        open={manualThemeOpen}
        onClose={() => setManualThemeOpen(false)}
        projectId={projectId}
        userId={userId}
        onCreated={(themeId) => setToast({ msg: `Theme "${themeId}" created`, severity: 'success' })}
      />

      {/* AI generator */}
      <ThemeGenerateDialog
        open={generateOpen}
        onClose={() => setGenerateOpen(false)}
        projectId={projectId}
        userId={userId}
        strategyService={strategyService}
        onSuccess={({ taskId }) => setToast({ msg: `AI generation scheduled — task ${taskId.slice(0, 6)}… Review in history below when ready.`, severity: 'info' })}
      />

      {/* Review dialog */}
      <GenerationReviewDialog
        open={Boolean(reviewRun)}
        onClose={() => setReviewRun(null)}
        projectId={projectId}
        entity="theme"
        run={reviewRun}
        titleSuffix={reviewRun ? `${reviewRun.generatedItemCount ?? 0} drafts` : undefined}
        onApply={handleApplyReviewedThemes}
        applyLabel={undefined}
        renderItem={(draft) => {
          const data = draft.data as unknown as ContentTheme & {
            description?: string;
            defaultMixPct?: number;
          };
          return (
            <Stack spacing={0.5}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Typography variant="subtitle2">{data.name ?? data.id}</Typography>
                <Chip size="small" label={data.id ?? draft.id} sx={{ fontFamily: 'monospace', fontSize: '0.7em' }} />
                <Chip size="small" label={`mix ${((data.defaultMixPct ?? 0) * 100).toFixed(0)}%`} />
              </Stack>
              {data.description && (
                <Typography variant="caption" color="text.secondary">{data.description}</Typography>
              )}
            </Stack>
          );
        }}
        renderItemDetails={(draft) => {
          const data = draft.data as unknown as ContentTheme & {
            description?: string;
            defaultMixPct?: number;
            archetypes?: string[];
            voiceHints?: string;
            dataUsage?: { items?: Array<{ sourcePath?: string; justification?: string }> };
          };
          const usageItems = data.dataUsage?.items ?? [];
          return (
            <Stack spacing={1.5}>
              {data.archetypes && data.archetypes.length > 0 && (
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    ARCHETYPES
                  </Typography>
                  <Stack direction="row" flexWrap="wrap" gap={0.5}>
                    {data.archetypes.map((a, i) => (
                      <Chip key={`${draft.id}_arch_${i}`} size="small" variant="outlined" label={a} />
                    ))}
                  </Stack>
                </Box>
              )}
              {data.voiceHints && (
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    VOICE HINTS
                  </Typography>
                  <Typography variant="body2">{data.voiceHints}</Typography>
                </Box>
              )}
              <DataUsageList
                items={usageItems}
                maxItems={20}
                testIdPrefix={`theme-review-data-${draft.id}`}
              />
              {usageItems.length === 0 && (
                <Alert severity="info" sx={{ py: 0 }}>
                  No source citations captured for this theme (older run or empty picker).
                </Alert>
              )}
            </Stack>
          );
        }}
      />

      {/* Toast */}
      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? <Alert severity={toast.severity} onClose={() => setToast(null)}>{toast.msg}</Alert> : <div />}
      </Snackbar>
    </Box>
  );
};

export default ThemesTab;
